import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Calendar, Monitor, Building2, Tag, LayoutTemplate, Activity, DollarSign } from 'lucide-react';
import { getMarketingAdById, getMarketingTableOptions } from '../../../api/nexus';
import KPICard from '../../../components/KPICard';
import AdCreativeThumbnail from '../../../components/mi/AdCreativeThumbnail';
import MiDetailLayout, {
  MiDetailError,
  MiDetailExternalButton,
  MiDetailLoading,
  MiDetailSection,
} from '../../../components/mi/MiDetailLayout';
import { formatTableDate } from '../../../utils/intelAxisTableUtils';

const platformLabel = (platform) => {
  if (platform === 'meta') return 'Meta';
  if (platform === 'google') return 'Google';
  return 'LinkedIn';
};

const formatMediaLabel = (mediaType) => {
  const raw = String(mediaType || '').trim();
  if (!raw) return '—';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

export default function MarketingAdDetails() {
  const { platform: platformParam, id } = useParams();
  const navigate = useNavigate();
  const platformValid = ['linkedin', 'meta', 'google'].includes(platformParam);
  const platform = platformParam === 'meta'
    ? 'meta'
    : platformParam === 'google'
      ? 'google'
      : 'linkedin';
  const [ad, setAd] = useState(null);
  const [playbackEnabled, setPlaybackEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMarketingAdById(platform, id)
      .then((res) => {
        if (cancelled) return;
        setAd(res?.data?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load ad');
        setAd(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [platform, id]);

  useEffect(() => {
    getMarketingTableOptions(platform)
      .then((res) => {
        setPlaybackEnabled(Boolean(res?.data?.data?.adVideoPlaybackEnabled));
      })
      .catch(() => { });
  }, [platform]);

  const backPath = sessionStorage.getItem('comply_last_marketing_path')
    || `/marketing/${platform}`;

  if (!platformValid) {
    return <Navigate to="/marketing/linkedin" replace />;
  }

  if (loading) {
    return <MiDetailLoading message="Loading ad details…" />;
  }

  if (error || !ad) {
    return (
      <MiDetailError
        message={error || 'Marketing ad not found.'}
        backLabel={`Back to ${platformLabel(platform)} Ads`}
        onBack={() => navigate(backPath)}
      />
    );
  }

  const displayTitle = ad.headline || ad.title || 'Ad creative';

  return (
    <MiDetailLayout
      backLabel={`Back to ${platformLabel(platform)} Ads`}
      onBack={() => navigate(backPath)}
      title={displayTitle}
      kpis={null}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(400px, 1.3fr) minmax(300px, 1fr)', gap: '24px', alignItems: 'start' }}>

        {/* Left Column: Post */}
        <div className="social-post-card">
          <div className="social-post-header">
            <div className="social-post-avatar">
              {ad.company ? ad.company.charAt(0).toUpperCase() : 'A'}
            </div>
            <div className="social-post-header-text">
              <div className="social-post-company">{ad.pageName || ad.company || 'Unknown'}</div>
              <div className="social-post-sponsored">Sponsored</div>
            </div>
          </div>

          {ad.adCopy ? (
            <div className="social-post-body">
              {ad.adCopy}
            </div>
          ) : null}

          <div className="social-post-creative">
            {ad.mediaUrl || ad.videoPlaybackUrl ? (
              <AdCreativeThumbnail
                ad={ad}
                playbackEnabled={playbackEnabled}
                eager
                imageClassName="social-post-image"
                fallbackClassName="social-post-image"
              />
            ) : (
              <div className="mi-detail-creative-empty">No preview available for this ad.</div>
            )}
          </div>

          <div className="social-post-footer">
            <div className="social-post-footer-text">
              <div className="social-post-footer-domain">{platformLabel(platform).toUpperCase()}</div>
              <div className="social-post-footer-title">{ad.headline || displayTitle}</div>
            </div>
            {ad.ctaLink ? (
              <a
                className="btn btn-sm"
                style={{ background: '#e4e6eb', border: 'none', color: '#050505', fontWeight: 600, padding: '8px 16px', whiteSpace: 'nowrap', flexShrink: 0 }}
                href={ad.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {ad.ctaText || 'Learn More'}
              </a>
            ) : null}
          </div>
        </div>

        {/* Right Column: Inspector Panel */}
        <div className="ad-inspector-panel">
          <div className="ad-inspector-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>About this Ad</span>
            {ad.adLibraryUrl ? (
              <a
                href={ad.adLibraryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-dark"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                 Visit Ad library <ExternalLink size={14} />
              </a>
            ) : null}
          </div>

          <div className="ad-inspector-section">
            <div className="ad-inspector-grid">

              <div className="ad-inspector-field full-width">
                <div className="ad-inspector-label"><Building2 size={14} /> Company</div>
                <div className="ad-inspector-value" style={{ fontSize: '15px' }}>{ad.company || '—'}</div>
              </div>

              <div className="ad-inspector-field">
                <div className="ad-inspector-label"><Monitor size={14} /> Platform</div>
                <div className="ad-inspector-value">{platformLabel(platform)}</div>
              </div>

              {platform === 'meta' && ad.pageName ? (
                <div className="ad-inspector-field">
                  <div className="ad-inspector-label"><LayoutTemplate size={14} /> Page</div>
                  <div className="ad-inspector-value">{ad.pageName}</div>
                </div>
              ) : null}

              <div className="ad-inspector-field">
                <div className="ad-inspector-label"><Tag size={14} /> Media Type</div>
                <div className="ad-inspector-value">{formatMediaLabel(ad.mediaType)}</div>
              </div>

              <div className="ad-inspector-field">
                <div className="ad-inspector-label"><Activity size={14} /> Status</div>
                <div className="ad-inspector-value">
                  <span className={`pill ${ad.isActive ? 'pill-green' : 'pill-outline'}`} style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', display: 'inline-block' }}>
                    {ad.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {ad.adStatus && ad.adStatus.toLowerCase() !== (ad.isActive ? 'active' : 'inactive') ? <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '4px', textTransform: 'uppercase' }}>{ad.adStatus}</div> : null}
                </div>
              </div>

              {ad.spendRange ? (
                <div className="ad-inspector-field full-width">
                  <div className="ad-inspector-label"><DollarSign size={14} /> Spend Range</div>
                  <div className="ad-inspector-value">
                    {ad.spendRange}
                    {ad.impressionRange ? <span style={{ display: 'block', fontSize: '12px', color: 'var(--text3)', marginTop: '2px' }}>{ad.impressionRange}</span> : null}
                  </div>
                </div>
              ) : null}

              <div className="ad-inspector-field">
                <div className="ad-inspector-label"><Calendar size={14} /> First Seen</div>
                <div className="ad-inspector-value">{formatTableDate(ad.dateFirstSeen)}</div>
              </div>

              <div className="ad-inspector-field">
                <div className="ad-inspector-label"><Calendar size={14} /> Last Seen</div>
                <div className="ad-inspector-value">{ad.lastSeenLabel || formatTableDate(ad.dateLastSeen)}</div>
              </div>

              {/* <div className="ad-inspector-field">
                <div className="ad-inspector-label"><Calendar size={14} /> Ingested</div>
                <div className="ad-inspector-value">{formatTableDate(ad.ingestedAt)}</div>
              </div> */}

            </div>
          </div>
        </div>
      </div>
    </MiDetailLayout>
  );
}

